"use client";

import { type ButtonHTMLAttributes, forwardRef } from "react";

type ButtonVariant = "primary" | "secondary" | "dark";

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    "btn-primary cursor-pointer disabled:cursor-wait disabled:opacity-70",
  secondary:
    "cursor-pointer rounded-lg border border-stone-300 bg-white font-medium text-stone-700 transition hover:bg-stone-50 disabled:cursor-wait disabled:opacity-70",
  dark: "cursor-pointer rounded-lg bg-stone-900 font-medium text-white transition hover:bg-stone-800 disabled:cursor-wait disabled:opacity-70",
};

const spinnerClasses: Record<ButtonVariant, string> = {
  primary:
    "h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white",
  secondary:
    "h-4 w-4 animate-spin rounded-full border-2 border-stone-200 border-t-stone-600",
  dark: "h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean;
  variant?: ButtonVariant;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      loading = false,
      variant = "primary",
      className = "",
      disabled,
      children,
      ...props
    },
    ref,
  ) {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        className={[
          variantClasses[variant],
          "inline-flex items-center justify-center gap-2",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
        {...props}
      >
        {loading ? (
          <span
            className={spinnerClasses[variant]}
            role="presentation"
            aria-hidden
          />
        ) : null}
        {children}
      </button>
    );
  },
);
